import * as t from '$trans';
import startsWith from 'lodash/startsWith';
import { match } from 'ts-pattern';
import { qs } from '$lib/UrlHelper';
import type { FeedPostTagEnum, MediaImageFragment } from '$gql/graphql.js';
import { zFeedPostTagEnum } from '$gql/zod-generated';
import type { ItemType } from './FeedPostForm/Dropdown.svelte';

export const COMMENTS_MAX_PER_PAGE = 10;

//export const POSTS_MAX_PER_PAGE = 10;

export const INITIAL_COMMENTS_TO_SHOW = 3;

export const FEED_POST_BODY_MAX_LENGTH = 400;

export const zTag = zFeedPostTagEnum;

export const TAG_ICONS: { [key in FeedPostTagEnum]?: string } = {
    info: 'fa fa-bullhorn',
    product_or_service: 'fa fa-shopping-cart',
    promo: 'fa fa-tags',
    job: 'fa fa-suitcase',
    event: 'tnp tnp-calendar',
    product: 'tnp tnp-shopping-cart',
    service: 'tnp tnp-employee',
    training: 'tnp tnp-graduation',
    real_estate_sales: 'tnp tnp-real-estate',
    real_estate_new: 'tnp tnp-real-estate',
    real_estate_rentals: 'tnp tnp-real-estate',
    real_estate_roommates: 'tnp tnp-real-estate',
    real_estate_offices_and_shops: 'tnp tnp-real-estate',
    vehicle: 'tnp tnp-car-rear',
    professional_equipment: 'tnp tnp-informatique',
} as const;

export function mimeTypeIsImage(mimeType: string) {
    return startsWith(mimeType, 'image/');
}

export function getVideoUrl(
    media: MediaImageFragment,
    options: { autoplay?: boolean | null } = {},
) {
    if (media.providerName.toLowerCase().includes('youtube')) {
        const params = new URLSearchParams();
        params.set('controls', '0');
        if (options.autoplay === true) {
            params.set('autoplay', '1');
        }

        return `https://www.youtube.com/embed/${media.providerReference}${qs(params)}`;
    } else if (media.providerName.toLowerCase().includes('vimeo')) {
        return `https://player.vimeo.com/video/${media.providerReference}?h=e9315dd11c&title=0&byline=0`;
    }

    return null;
}

export function findPage(itemsPerPage: number, itemIndex: number) {
    let pageNumber = Math.floor(itemIndex / itemsPerPage);
    if (itemIndex % itemsPerPage !== 0) {
        pageNumber += 1;
    }

    return pageNumber;
}

export type CommentData = {
    commentIndex: number | null;
    commentId: number | null;
    parentIndex: number | null;
    parentId: number | null;
};

export const urlForFormat = (format: string | null, media: MediaImageFragment | null) => {
    if (media == null) {
        return '';
    }

    if (format == null) format = 'reference';

    return format === 'reference'
        ? media.referenceUrl
        : media.fakeUrl.replace('__fake__', format);
};

export function categoryItems(lang: string): ItemType<FeedPostTagEnum>[] {
    return [
        {
            label: t.feed_post.category_classifieds({}, lang),
            value: 'classifieds',
            icon: 'tnp tnp-list',
            children: [
                {
                    label: t.feed_post.category_real_estate({}, lang),
                    value: 'real_estate',
                    children: [
                        {
                            label: t.feed_post.category_real_estate_sales({}, lang),
                            value: 'real_estate_sales',
                        },
                        {
                            label: t.feed_post.category_real_estate_new({}, lang),
                            value: 'real_estate_new',
                        },
                        {
                            label: t.feed_post.category_real_estate_rentals({}, lang),
                            value: 'real_estate_rentals',
                        },
                        {
                            label: t.feed_post.category_real_estate_roommates({}, lang),
                            value: 'real_estate_roommates',
                        },
                        {
                            label: t.feed_post.category_real_estate_offices_and_shops(
                                {},
                                lang,
                            ),
                            value: 'real_estate_offices_and_shops',
                        },
                    ],
                },
                {
                    label: t.feed_post.category_vehicle({}, lang),
                    value: 'vehicle',
                    children: [
                        {
                            label: t.feed_post.select_tag({ tag: 'vehicle__cars' }, lang),
                            value: 'vehicle__cars',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'vehicle__motorcycles',
                                },
                                lang,
                            ),
                            value: 'vehicle__motorcycles',
                        },
                        {
                            label: t.feed_post.select_tag(
                                { tag: 'vehicle__trucks' },
                                lang,
                            ),
                            value: 'vehicle__trucks',
                        },
                        {
                            label: t.feed_post.select_tag(
                                { tag: 'vehicle__utilities' },
                                lang,
                            ),
                            value: 'vehicle__utilities',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'vehicle__boats_boating',
                                },
                                lang,
                            ),
                            value: 'vehicle__boats_boating',
                        },
                    ],
                },
                {
                    label: t.feed_post.category_professional_equipment({}, lang),
                    value: 'professional_equipment',
                    children: [
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__tractors',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__tractors',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__agricultural_material',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__agricultural_material',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__construction_structural_work',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__construction_structural_work',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__heavyweight',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__heavyweight',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__lifting_equipment',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__lifting_equipment',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__industrial_equipment',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__industrial_equipment',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__equipment_for_restaurants_and_hotels',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__equipment_for_restaurants_and_hotels',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__equipment_for_businesses',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__equipment_for_businesses',
                        },
                        {
                            label: t.feed_post.select_tag(
                                {
                                    tag: 'professional_equipment__medical_equipment',
                                },
                                lang,
                            ),
                            value: 'professional_equipment__medical_equipment',
                        },
                    ],
                },
            ],
        },
        {
            label: t.feed_post.category_product({}, lang),
            value: 'product',
            icon: 'tnp tnp-shopping-cart',
            children: [
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__computer_science' },
                        lang,
                    ),
                    value: 'product__computer_science',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__phones_and_tablets' },
                        lang,
                    ),
                    value: 'product__phones_and_tablets',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__tv_and_hi_tech' },
                        lang,
                    ),
                    value: 'product__tv_and_hi_tech',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__home_and_kitchen' },
                        lang,
                    ),
                    value: 'product__home_and_kitchen',
                },
                {
                    label: t.feed_post.select_tag(
                        {
                            tag: 'product__furniture_an_doffice',
                        },
                        lang,
                    ),
                    value: 'product__furniture_an_doffice',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__home_appliance' },
                        lang,
                    ),
                    value: 'product__home_appliance',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__clothing_and_shoes' },
                        lang,
                    ),
                    value: 'product__clothing_and_shoes',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__beauty_andhealth' },
                        lang,
                    ),
                    value: 'product__beauty_andhealth',
                },
                {
                    label: t.feed_post.select_tag(
                        {
                            tag: 'product__video_games_and_consoles',
                        },
                        lang,
                    ),
                    value: 'product__video_games_and_consoles',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__sports_and_leisure' },
                        lang,
                    ),
                    value: 'product__sports_and_leisure',
                },
                {
                    label: t.feed_post.select_tag(
                        { tag: 'product__baby_and_toys' },
                        lang,
                    ),
                    value: 'product__baby_and_toys',
                },
                {
                    label: t.feed_post.select_tag(
                        {
                            tag: 'product__garden_and_animals_and_outdoors',
                        },
                        lang,
                    ),
                    value: 'product__garden_and_animals_and_outdoors',
                },
            ],
        },
        {
            label: t.feed_post.category_event({}, lang),
            value: 'event',
            icon: 'tnp tnp-calendar',
        },

        {
            label: t.feed_post.category_info({}, lang),
            value: 'info',
            icon: 'tnp tnp-megaphone-outlined',
        },
        {
            label: t.feed_post.category_promo({}, lang),
            value: 'promo',
            icon: 'tnp tnp-tags-outlined',
        },
        {
            label: t.feed_post.category_service({}, lang),
            value: 'service',
            icon: 'tnp tnp-employee',
        },
        {
            label: t.feed_post.category_job({}, lang),
            value: 'job',
            icon: 'tnp tnp-briefcase-1',
        },
        {
            label: t.feed_post.category_training({}, lang),
            value: 'training',
            icon: 'tnp tnp-graduation',
        },
    ];
}

export function getDeepTypesFromTag(tag: FeedPostTagEnum): readonly FeedPostTagEnum[] {
    return match(tag)
        .with(
            'classifieds',
            () =>
                [
                    'classifieds',
                    ...getDeepTypesFromTag('professional_equipment'),
                    ...getDeepTypesFromTag('vehicle'),
                    ...getDeepTypesFromTag('real_estate'),
                ] as const,
        )
        .with(
            'real_estate',
            () =>
                [
                    'real_estate',
                    'real_estate_sales',
                    'real_estate_new',
                    'real_estate_rentals',
                    'real_estate_roommates',
                    'real_estate_offices_and_shops',
                ] as const,
        )
        .with(
            'vehicle',
            () =>
                [
                    'vehicle',
                    'vehicle__cars',
                    'vehicle__motorcycles',
                    'vehicle__trucks',
                    'vehicle__utilities',
                    'vehicle__boats_boating',
                ] as const,
        )
        .with(
            'professional_equipment',
            () =>
                [
                    'professional_equipment',
                    'professional_equipment__tractors',
                    'professional_equipment__agricultural_material',
                    'professional_equipment__construction_structural_work',
                    'professional_equipment__heavyweight',
                    'professional_equipment__lifting_equipment',
                    'professional_equipment__industrial_equipment',
                    'professional_equipment__equipment_for_restaurants_and_hotels',
                    'professional_equipment__equipment_for_businesses',
                    'professional_equipment__medical_equipment',
                ] as const,
        )
        .with(
            'product',
            () =>
                [
                    'product',
                    'product__computer_science',
                    'product__phones_and_tablets',
                    'product__tv_and_hi_tech',
                    'product__home_and_kitchen',
                    'product__furniture_an_doffice',
                    'product__home_appliance',
                    'product__clothing_and_shoes',
                    'product__beauty_andhealth',
                    'product__video_games_and_consoles',
                    'product__sports_and_leisure',
                    'product__baby_and_toys',
                    'product__garden_and_animals_and_outdoors',
                ] as const,
        )
        .otherwise(() => [tag] as const);
}
